import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  DeleteTableRowButton,
  Table,
  TableOperationButton,
  TokenType,
} from '@atlas-frontend/sdk';
import { createColumnHelper } from '@tanstack/react-table';
import { Flex } from 'antd';
import { useMemo } from 'react';
import { colorGray } from '@atlas-frontend/design-tokens';
import * as Icon from 'react-feather';
import translations from './TokenTable.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory('token-table');
addTranslations(translations);

const columnHelper = createColumnHelper<TokenType>();

export interface TokenTableProps {
  data: TokenType[];
  isLoading: boolean;
  onAddToken: () => void;
  onDeleteToken: (id: number) => void;
}

export function TokenTable({
  data,
  isLoading,
  onAddToken,
  onDeleteToken,
}: TokenTableProps) {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t('column.name'),
        enableColumnFilter: false,
        meta: {
          minWidth: '30ch',
        },
      }),
      columnHelper.display({
        id: 'operations',
        header: () => (
          <Flex justify="center" gap="small">
            <TableOperationButton
              id="create-token"
              icon={<Icon.Plus size={22} color={colorGray[7]} />}
              onClick={onAddToken}
              title={t('button.create-token')}
            />
          </Flex>
        ),
        cell: (props) => (
          <Flex justify="center" gap="small">
            <DeleteTableRowButton
              id={`delete-table-row-${props.row.index}`}
              confirm={{
                onConfirm: () => {
                  onDeleteToken(props.row.original.id);
                },
              }}
            />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          width: 100,
          className: 'sticky-right',
        },
      }),
    ],
    [onAddToken, onDeleteToken, t],
  );

  return (
    <Table
      name="token-table"
      data={data}
      columns={columns}
      scrollContainerSelector="#tokens-content"
      sticky
      virtual
      isLoading={isLoading}
    />
  );
}
